import { Component, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { UtilityService } from 'src/app/_services/utility.service';
import Swal, { SweetAlertIcon } from 'sweetalert2';

@Component({
  selector: 'app-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.css']
})
@Injectable()
export class BaseComponent {
  public router = inject(Router);
  protected readonly utilityService = inject(UtilityService);

  constructor() {}

  beforeUnloadHandler(event: any) {
    event.preventDefault();
    event.returnValue = '';
    const message =
      'Hai modifiche non salvate. Sei sicuro di voler confermare?';
    event.returnValue = message;
    return message;
  }

  beforeUnload() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  resetBeforeUnload() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  // funzione da utilizzare nei click event, per i quali SonarLint richiede un metodo keydown
  checkKeyEvent(event: Event): boolean {
    const isKeyboardEvent =
      event instanceof KeyboardEvent &&
      (event?.key === ' ' || event?.key === 'Enter');
    const isMouseEvent = event instanceof MouseEvent && event?.button === 0;
    const isTouchEvent =
      typeof TouchEvent !== 'undefined' && event instanceof TouchEvent;

    if (!isKeyboardEvent && !isMouseEvent && !isTouchEvent) {
      return false;
    }
    return true;
  }

  /**
   *
   * @param path il path di reindirizzamento alla pagina
   * @param event l'evento al click
   * @returns
   */
  goTo(path: string, event: Event) {
    if (!this.checkKeyEvent(event)) {
      return;
    }
    event.preventDefault();
    this.router.navigate([path]);
  }

  /**
   * Alert generico per mostrare sia l'avvenuta azione
   * sia un errore o un avviso.
   * @param type success | error | warning
   * @param title messaggio da visualizzare
   */
  responseToast(type: SweetAlertIcon, title: string) {
    const iconColorMap: Record<SweetAlertIcon, string> = {
      success: '#3dc93d',
      error: '#ff4747',
      warning: '#f1c40f',
      info: '#3498db',
      question: '#9b59b6'
    };

    return Swal.fire({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      icon: type,
      iconColor: iconColorMap[type],
      title: title,
      didOpen: toast => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  /**
   * Alert che gestisce la conferma su specifiche azioni, come ad esempio
   * l'eliminazione di un elemento
   * @param textConfirm testo da visualizzare nel toast alla richiesta di conferma
   * @param apiLogic la logica da attivare alla conferma
   */
  confirmToast(apiLogic: () => void, textConfirm: string) {
    Swal.fire({
      text: textConfirm,
      showCancelButton: true,
      cancelButtonText: 'Annulla',
      cancelButtonColor: '#00337C',
      showConfirmButton: true,
      confirmButtonText: 'Conferma',
      confirmButtonColor: '#FFB26B',
      reverseButtons: true,
      focusConfirm: false,
      customClass: {
        popup: 'main-font'
      },
      backdrop: '#f4f3efad',
      position: 'center'
    }).then(result => {
      if (result.isConfirmed) {
        apiLogic();
      }
    });
  }

  /**
   * Funzione di utility per disabilitare la selezione di date passate
   * @param current data da controllare
   * @returns boolean se disabilitare la data
   */
  disabledOldDate = (current: Date): boolean => {
    return moment(current).isBefore(moment(), 'day');
  };

  /**
   * Metodo che serve per disabilitare tutti i sabati, domeniche, festività e vecchie date
   * @param current data odierna
   * @returns boolean
   */
  disablePastAndWeekendDates = (
    current: Date,
    allowToday: boolean = false
  ): boolean => {
    const today = moment();
    const date = moment(current);

    const isPastOrToday = allowToday
      ? date.isBefore(today, 'day')
      : date.isSameOrBefore(today, 'day');

    return isPastOrToday || this.utilityService.disabledDate(current);
  };
}
